import { useCallback } from 'react';
import { useLogin, useLoginWithSiws } from '@privy-io/react-auth';
import { notify } from 'src/utils/notifications';
import { ConnectedStandardSolanaWallet } from '@privy-io/react-auth/solana';
import useSyncedRef from 'src/hooks/utils/useSyncedRef';

/**
 * Authenticates the external wallet to sign in to Privy.
 * E.g. Using your Phantom Solana wallet as a sign-in method to your Privy account.
 *
 * For Ledger wallets, uses Privy's modal-based login with `isSigningInWithLedgerSolana: true`
 * which handles transaction signing instead of message signing (Ledger doesn't support message signing on Solana).
 */
export const useAuthenticateSolanaEoaWallet = () => {
	const { generateSiwsMessage, loginWithSiws } = useLoginWithSiws();
	const { login: loginWithModal } = useLogin();

	const generateSiwsMessageRef = useSyncedRef(generateSiwsMessage);
	const loginWithSiwsRef = useSyncedRef(loginWithSiws);

	const handleAuthenticateSolanaEoaWallet = useCallback(
		async (
			eoaWallet: ConnectedStandardSolanaWallet,
			isLedger: boolean
		): Promise<boolean> => {
			if (!eoaWallet) {
				notify({
					type: 'error',
					message: 'No EOA wallet found',
				});
				return false;
			}

			try {
				if (isLedger) {
					// Use Privy's modal-based login with Ledger flag which handles transaction signing
					// instead of message signing (Ledger doesn't support message signing on Solana)
					loginWithModal({ isSigningInWithLedgerSolana: true });
					// Note: This opens a modal and is async, the actual login completion
					// will be handled by Privy's internal state management
					return true;
				}

				// Manual SIWS flow for non-Ledger wallets
				const message = await generateSiwsMessageRef.current({
					address: eoaWallet.address,
				});
				const encodedMessage = new TextEncoder().encode(message);
				const signMessageResult = await eoaWallet.signMessage({
					message: encodedMessage,
				});

				const signatureString = btoa(
					String.fromCharCode(...signMessageResult.signature)
				);

				await loginWithSiwsRef.current({
					message,
					signature: signatureString,
				});

				return true;
			} catch (error) {
				console.error(error);

				return false;
			}
		},
		[loginWithModal]
	);

	return {
		handleAuthenticateSolanaEoaWallet,
	};
};
